import {Button, Link, useNavigate} from '@startlibs/components';
import {formFetch, getFetcher} from '@startlibs/utils';
import {Errors, Field, TextInput, validation, WithForm} from '@startlibs/form';
import {useToggle} from '@startlibs/core'
import React, {useEffect, useMemo, useRef} from 'react'
import {jwtDecode} from 'jwt-decode'
import styled from 'styled-components'
import {FormattedMessage, useIntl} from "react-intl";
import {
  AdditionalActionButtons,
  Card,
  CardActionButton,
  CardHeader,
  LinkSSO,
  SignInLayout,
  SignInMessageBox
} from '../components/SigninLayout';
import {JwtAuthenticationWithLocale} from './JwtAuthentication'
import {PasswordInput} from '../components/PasswordInput'
import {RedefinePasswordForm} from './RedefinePassword'
import {SupportMessage} from '../components/SupportMessage'
import {TwoFactorAuthentication} from './TwoFactorAuthentication'
import {required} from '../lib/validation'


//max-width: ${props => props.accountSettings ? `39rem` : `35rem`};
const SignInCard = styled(Card)`
  max-width: ${props => props.accountSettings ? `42rem` : `40rem`};
  margin: auto;
  transition: 0.25s ease;
`

const UNASSOCIATED_SYSTEM_ERROR = 'User user is not associated with a system.'
const INVALID_CREDENTIALS = (intl) => intl.formatMessage({
  defaultMessage: 'Invalid credentials',
  description: 'Login error message when credentials are invalid'
})

export const loginFailure = (intl) => ({detail, message, params, status}, passwordError = INVALID_CREDENTIALS(intl)) =>
  (status === 401 && detail === UNASSOCIATED_SYSTEM_ERROR && {'': [passwordError]}) ||
  (message === 'error.suspended' && {
    '': [intl.formatMessage({
      defaultMessage: "This account has been suspended. Please contact your administrator.",
      description: "Login error message when account is suspended"
    })]
  }) ||
  (message === 'error.locked' && {
    '': [<span><FormattedMessage
      defaultMessage="This account has been locked. Please check {hasEmail, select, true {<b>{email}</b>} other {your email}} and click the link to unlock."
      description="Login error message when account is locked"
      values={{
        email: params.email,
        hasEmail: !!params.email,
        b: (...chunks) => <b>{chunks}</b>
      }}
    /></span>]
  }) ||
  (message === 'error.notActivated' && {
    '': [<span>
      <FormattedMessage
        defaultMessage="<b>Account not activated yet</b>. We just sent a new activation link to {hasEmail, select, true {<b>{email}</b>} other {your email}}. <detail>Please check your spam folder if necessary.</detail>"
        description="Login error message when account is not activated"
        values={{
          email: params.email,
          hasEmail: !!params.email,
          detail: (...chunks) => <span className="error-detail">{chunks}</span>,
          b: (...chunks) => <b>{chunks}</b>
        }}
      /></span>]
  }) ||
  ((message === 'error.validation' || message === 'error.http.401') && {'currentPassword': [passwordError]})


export const LoginForm = ({system, fromActivation, loginToSettings, needsRegistration, autoLogin, userToggle}) => {
  const loading = useToggle()
  const expiredPassword = useToggle()
  const verify2fa = useToggle()
  const accountSettings = useToggle()
  const intl = useIntl()
  const navigate = useNavigate()
  const formRef = useRef()

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), [])
  const jwt = searchParams.has("jwt") ? jwtDecode(searchParams.get("jwt")) : {}

  const onFailure = validation.responseFailure((response) => {
    if (response.status === 403) {
      passwordExpired(response.params.key)
    } else {
      // If the SSO option is enabled, when the email is not @purview, it will redirect to the SSO login page
      if (response.url) {
        loading.open()
        window.location = response.url
        return {}
      }
      return loginFailure(intl)(response)
    }
  })

  const passwordExpired = (key) => {
    verify2fa.close()
    expiredPassword.openWith(key)
  }

  const onSuccess = (_, {idToken, id_token, username, preAuthToken}) => {
    if (username) {
      needsRegistration(idToken, username)
    } else if (preAuthToken) {
      verify2fa.openWith(preAuthToken)
    } else {
      concludeLogin(id_token)
    }
  }

  const concludeLogin = (id_token) => {
    if (accountSettings.isOpen) {
      loading.open()
      getFetcher('/pasapi/account/info', null, {headers: new Headers({'Authorization': `Bearer ${id_token}`})})
        .then((result) => {
          userToggle.openWith({...result, id_token})
          navigate('/account?systemId=' + system.systemId + "&locale=" + intl.locale)
        })
    } else {
      loading.open()
      window.location = system.systemWebHooks.systemAuthWebHook + id_token + "&locale=" + intl.locale
    }
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get("loginFailure") === 'true' || urlParams.get('newRegistration')) {
      formRef.current.setErrors({"password": [INVALID_CREDENTIALS(intl)]})
    }
    if (urlParams.get("preAuthToken")) {
      verify2fa.openWith(urlParams.get("preAuthToken"))
    }
    if (loginToSettings) {
      accountSettings.open()
    }
  }, []);

  const values = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return {
      ...(urlParams.get("username") ? {username: urlParams.get("username")} : {}),
      systemId: system.systemId,
      rememberMe: false
    }
  }, [])


  const expiredSession = location.state && location.state.expiredSession
  const preValidation = validation.buildValidation({
    password: required(intl),
    username: required(intl)
  })

  if (verify2fa.isOpen) {
    return <TwoFactorAuthentication
      system={system}
      userToggle={userToggle}
      preAuthToken={verify2fa.isOpen}
      concludeLogin={concludeLogin}
      passwordExpired={passwordExpired}
      username={formRef.current?.getValue('username') || values.username}
    />
  }

  if (jwt.sub && !expiredPassword.isOpen) {
    return <JwtAuthenticationWithLocale
      passwordExpired={passwordExpired}
      needsRegistration={needsRegistration}
      system={system}
      verify2fa={verify2fa}
      parsedJwt={jwt}
      jwt={searchParams.get("jwt")}
      autoLogin={autoLogin}
      userToggle={userToggle}
    />
  }

  const ssos = system.ssoEntities || []

  return (
    <SignInLayout hideSupportMessage system={system}>
      {
        fromActivation &&
        <SignInMessageBox>
          <FormattedMessage
            defaultMessage="Email address successfully updated."
            description="Login form, message displayed after email address update"
          />
        </SignInMessageBox>
      }
      {
        expiredSession &&
        <SignInMessageBox warning>
          <FormattedMessage
            defaultMessage="Your session has expired. Please sign in again."
            description="Login form, message displayed after session expiration"
          />
        </SignInMessageBox>
      }
      {!expiredPassword.isOpen ?
        <SignInCard accountSettings={accountSettings.isOpen}>
          {accountSettings.isOpen ?
            <CardHeader>
              <h1><FormattedMessage
                defaultMessage="User authentication settings" description="Login form, authentication settings header"
              /></h1>
              <p><FormattedMessage
                defaultMessage="Enter your credentials to manage your account"
                description="Login form, authentication settings instructions"
              /></p>
            </CardHeader>
            :
            <CardHeader>
              <h1><FormattedMessage
                defaultMessage="Sign in to {systemName}" description="Login form header" values={{
                systemName: system.systemName
              }}
              /></h1>
              <p><FormattedMessage
                defaultMessage="Enter your details below" description="Login form header"
              /></p>
            </CardHeader>
          }
          <WithForm
            alwaysSave
            values={values}
            preValidation={preValidation}
            onSuccess={onSuccess}
            onFailure={onFailure}
            action={formFetch(`/pasapi/authenticate?locale=${intl.locale}`)}
            ref={formRef}
          >{form => <>
            <TextInput
              name="username"
              autoComplete="off" tabIndex={1} label={system.labels.loginInput || intl.formatMessage({
              defaultMessage: "Username:",
              description: "Login form, username field label"
            })} placeholder={intl.formatMessage({
              defaultMessage: "Enter your username",
              description: "Login form, username field placeholder"
            })} path="username"
            />
            <Field
              label={intl.formatMessage({
                defaultMessage: "Password:",
                description: "Login form, password field label"
              })}
            >
              <Link
                className={(form.hasErrors() ? 'has-error ' : '') + 'password-link'} to="/recovery" tabIndex={2}
              ><FormattedMessage
                defaultMessage="Forgot password?" description="Login form, forgot password link"
              /></Link>
              <PasswordInput
                name="password"
                autoComplete="off"
                tabIndex={1}
                withoutField
                autoFocus={!!values.username}
                path="password"
                placeholder={intl.formatMessage({
                  defaultMessage: "Enter your password",
                  description: "Login form, password field placeholder"
                })}
              />
            </Field>
            {/*{false && !accountSettings.isOpen &&
              <div className="checkbox-list">
                <SimpleCheckbox
                  form={form}
                  label="Keep me signed in while in this computer"
                  path="rememberMe"
                />
              </div>}*/}
            <Errors/>
            <CardActionButton
              highlight isLoading={form.isLoading || loading.isOpen} tabIndex={1} type="submit"
            >{accountSettings.isOpen
              ? intl.formatMessage({
                defaultMessage: 'Sign in to settings',
                description: 'Login form, sign in to settings button'
              })
              : intl.formatMessage({
                defaultMessage: 'Sign in',
                description: 'Login form, sign in button'
              })}</CardActionButton>
            {ssos.map(sso => <LinkSSO href={sso.url}>{sso.label || intl.formatMessage({
              defaultMessage: "Use single sign-on (SSO) instead",
              description: "Login form, SSO link"
            })}</LinkSSO>)}
          </>}</WithForm>
        </SignInCard> :
        <ExpiredPassword system={system} tokenKey={expiredPassword.isOpen}/>
      }
      <SupportMessage system={system}/>
      {!expiredPassword.isOpen && !loginToSettings &&
        <AdditionalActionButtons>
          <Button outline small onClick={accountSettings.toggle}>{accountSettings.isOpen ?
            <span><FormattedMessage
              defaultMessage="Back to sign in" description="Login form, back to sign in button"
            /></span> : intl.formatMessage({
              defaultMessage: 'Authentication settings',
              description: 'Login form, authentication settings button'
            })}</Button>
        </AdditionalActionButtons>
      }
    </SignInLayout>
  )
}

const ExpiredPassword = (props) => <RedefinePasswordForm
  {...props} header={() =>
  <CardHeader>
    <h1><FormattedMessage
      defaultMessage="Your password has expired" description="Expired password form header"
    /></h1>
    <p><FormattedMessage
      defaultMessage="Please set a new password to continue" description="Expired password form instructions"
    /></p>
  </CardHeader>
}
/>
